import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  // Base: tighter, brand-friendly, better focus ring
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 " +
    "ring-offset-background [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Brand default = gradient primary
        default:
          "bg-gradient-to-r from-brand-primary to-brand-secondary text-white hover:opacity-90 active:opacity-95",

        // Solid primary (non-gradient) if you prefer a flat look in some places
        primary:
          "bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/95",

        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 active:bg-destructive/95",

        outline:
          "border border-brand-border bg-background text-brand-text hover:bg-accent hover:text-accent-foreground",

        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 active:bg-secondary/90",

        // Subtle ghost with SmartCare pastel hover
        ghost:
          "text-brand-text hover:bg-brand-tile-info/60 active:bg-brand-tile-info/80",

        // Plain variant: minimal so caller can supply full background via className
        plain: "bg-transparent text-inherit hover:opacity-95 active:opacity-95",

        link: "text-primary underline-offset-4 hover:underline",

        // Soft button for tiles/panels
        subtle:
          "bg-brand-tile-accent text-violet-900 hover:bg-violet-100 active:bg-violet-200",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 px-3",
        lg: "h-11 px-6",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default", // gradient by default
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
